import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { invalidateDeviation, useAddDeviation } from '../../../api/deviation'
import { invalidateMainComponentCache } from '../../../api/main-component'
import { invalidateMooringCache, useGetMooring } from '../../../api/moorings'
import { DeviationStepperForm } from '../../../forms/DeviationStepperForm/DeviationStepperForm'
import { IComponent, IPosition, IMooring } from '../../../generated-types'
import { IDeviationType } from '../../../generated-types/deviation-type'
import { IMainComponent } from '../../../generated-types/main-component'
import { ShowDialogProps } from '../../../show-dialog'
import { IDeviationFormWizardType } from '../../../generated-types/form-wizard'
import { invalidatePositionCache, useRegenerateLocalityMap } from 'src/api/positions'
import { invalidateComponentsCache } from 'src/api/components'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidateScheduledMaintenanceCache } from 'src/api/scheduled-maintenance'

export interface ICreateDeviationDialogProps extends ShowDialogProps {
  customerId: EntityId
  localityId: EntityId
  mooringId?: EntityId
  positionId?: EntityId
  component?: IComponent
  mainComponent?: IMainComponent
  position?: IPosition
  disableSteps?: any
  type: IDeviationType
  formWizard?: IDeviationFormWizardType
  scheduledTaskId?: EntityId
  maintenanceId?: number
  deviationTypeFromParent: IDeviationType
  initialValues?: Record<string, unknown>
  showExistingDeviations?: boolean
}

export const CreatePositionDeviationDialog: React.FC<ICreateDeviationDialogProps> = ({
  pop,
  customerId,
  localityId,
  mooringId,
  disableSteps = true,
  type,
  position,
  positionId,
  component,
  mainComponent,
  formWizard,
  scheduledTaskId,
  maintenanceId,
  deviationTypeFromParent,
  initialValues,
  showExistingDeviations,
}) => {
  const { t } = useTranslation(['locality', 'common'])
  const { data: mooring } = useGetMooring(customerId as EntityId, localityId as EntityId, mooringId as EntityId, {
    isIgnoreQueryFilters: sessionStorage.getItem('readModeEnabledLocalityId') == localityId,
  })

  const { mutateAsync, isLoading } = useAddDeviation(customerId)
  const { mutateAsync: regenerateMap } = useRegenerateLocalityMap(customerId as EntityId, localityId as EntityId)

  const onSubmit = useCallback(
    async form => {
      if (type === IDeviationType.Equipment || type === IDeviationType.MainComponent) {
        delete form.localityId
        delete form.mooringId
      }
      await mutateAsync(form, {
        onSuccess: async () => {
          invalidatePositionCache.getCagePositionDeviations(customerId, localityId, positionId)
          invalidateDeviation.invalidateAllDeviations(
            customerId as EntityId,
            localityId,
            mainComponent?.id ?? undefined,
          )
          invalidateMainComponentCache.getDeviationCounts(customerId as EntityId, mainComponent?.id as EntityId)
          invalidateMainComponentCache.getMainComponentById(customerId as EntityId, mainComponent?.id as EntityId)
          invalidateComponentsCache.getComponents(customerId as EntityId, positionId as EntityId)
          invalidateDeviation.invalidateAllDeviations(customerId as EntityId, localityId)
          invalidateMooringCache.getMooringsForMap(customerId as EntityId, localityId)
          invalidationActivityLogCache.getCageActivityLogs()
          invalidateCustomer.getCustomersActivityCounts(customerId)
          invalidateLocalityCache.getLocalityWithActivityCounts(customerId as EntityId, localityId as EntityId)
          invalidateDeviation.getAllDeviationsForScheduledWorkOrders(customerId as EntityId, localityId as EntityId)
          invalidateLocalityCache.getMaintenances(customerId, localityId)
          invalidateLocalityCache.getMaintenanceTypesCount(customerId, localityId)
          invalidateMainComponentCache.getMainComponents(customerId as EntityId, localityId as EntityId)
          invalidateScheduledMaintenanceCache.getMaintenanceList(customerId as EntityId, localityId as EntityId)
          toast.success(t('added-deviation-successfully-toast', { ns: 'deviation' }))
          pop()
          regenerateMap({
            onSuccess: () => { },
            onError: () => { },
          })
        },
      })
    },
    [customerId, localityId, mainComponent?.id, mutateAsync, pop, positionId, t, regenerateMap, type],
  )

  return (
    <>
      {formWizard == IDeviationFormWizardType.Position ? (
        <DeviationStepperForm
          onSubmit={onSubmit}
          onCancel={() => pop()}
          customerId={customerId}
          localityId={localityId}
          mooring={mooring ?? ({ id: mooringId } as IMooring)}
          isDisableWizard={disableSteps}
          type={type}
          formWizard={formWizard}
          positionId={position?.id}
          position={position}
          title={t('addNewDeviation', { ns: 'deviation' })}
          isLoading={isLoading}
          deviationTypeFromParent={deviationTypeFromParent}
        />
      ) : (
        <DeviationStepperForm
          onSubmit={onSubmit}
          onCancel={() => pop()}
          customerId={customerId}
          localityId={localityId}
          mooring={mooring ?? ({ id: mooringId } as IMooring)}
          positionId={positionId}
          position={position}
          component={component}
          mainComponent={mainComponent}
          isDisableWizard={disableSteps}
          type={type}
          formWizard={formWizard}
          title={t('addNewDeviation', { ns: 'deviation' })}
          isLoading={isLoading}
          scheduledTaskId={scheduledTaskId}
          maintenanceId={maintenanceId}
          deviationTypeFromParent={deviationTypeFromParent}
          initialValues={initialValues}
          showExistingDeviations={showExistingDeviations}
        />
      )}
    </>
  )
}
